import { Suspense, useContext, useState } from 'react';
import { SmartSendContext } from '../../../lib/context/SmartSendContext';
import {
  ApplyStatusToSelectedAction,
  ArrangeReturnCollectionAction,
  BulkSaveSelectedAction
} from '../../../lib/context/SmartSendContextModels';
import { SmartSendSummary } from '../../../lib/helpers/SmartSendFilterApplier';
import { ApplierOption } from '../../../lib/helpers/ApplierOption';
import Tags from './Tags';
import ApplyDimensions from './ApplyDimensions';
import ApplyService from './ApplyService';
import { Dimensions, SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import Extras from './Extras';
import { useEffect } from 'react';
import { ExportToCsv } from '../../csv/ExportToCsv';
import { GetSelectionBasedOnAction } from '../../../lib/helpers/GetSelectionBasedOnAction';
import {
  SmartSendCsvExportSoldHeaders,
  MapSmartSendSoldItemToCsv,
  SmartSendCsvExportBookedHeaders,
  SmartSendCsvExportOrderHeaders,
  MapOrderToCsv,
  MapSmartSendBookedItemToCsv
} from '../../../lib/helpers/CsvMapperHelper';
import Restore from './Restore';
import Delete from './Delete';
import SaveForLater from './SaveForLater';
import ApplyRecipientContactDetails from './ApplyContactDetails';
import { GetSelectedItems } from '../../../lib/helpers/GetSelectedItems';
import ConfirmReturnCollection from './ConfirmReturnCollection';
import { GetBulkActionTitle } from './GetBulkActionTitle';
import ApplyPreferredCollectionDate from './ApplyPreferredCollectionDate';
import DownloadLabels from './DownloadLabels';
import Archive from './Archive';
import SmartSendActionList from './SmartSendActionList';
import ConfirmDispatch from './ConfirmDispatch';
import { EventSections } from '../../../lib/events/EventLogger';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { ProtectionExtraIds } from '../../ExtrasHelper';
import Protection from './Protection';
import { Tagged } from '../../Tagged';
import { PanelButton } from '../panels/PanelButtons';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { P2GSidePanel } from '~/components/sidepanel';
import { P2GBox } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import ApplyTariffCodes from './ApplyTariffCodes';
import GenerateOptions from '~/apps/smartsend/lib/helpers/SmartSendBulkActionOptions';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import { generateProtectionKey } from '../generateProtectionKey';

interface props {
  open: boolean;
  action?: string;
  onClose: () => void;
  filter: SmartSendSummary;
  filterKey: string;
  exclude: ApplierOption[];
  smartSendItemsCache: SmartSendItemApiModel[];
  onActionClick: (a: ApplierOption, close: () => void) => void;
}
export default function SmartSendSoldItemsAddAction(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const { createRule, getQuoteProviders, getRules, getServiceTags, getTags, updateRule, updateServiceTags } =
    useSmartSendApi();
  const ctx = useContext(SmartSendContext);
  const { track } = useSmartSendAnalytics();
  const { getQuoteOptions, getPackages } = useSmartSendApi();
  const [action, setAction] = useState('LIST');

  useEffect(() => {
    setAction(props.action);
  }, [props.action]);

  const affected = (action: ApplierOption) => {
    return props.filter.bulkOptions.filter((o) => o === action).length;
  };

  const onActionClick = (a: ApplierOption) => {
    props.onActionClick(a, close);
    setAction(a);
  };

  const reset = () => {
    setAction('LIST');
  };

  const close = () => {
    reset();
    props.onClose();
  };

  const trackBulkActionApplied = (payload: Record<string, unknown>) => {
    track({
      type: 'bulk_action_applied',
      payload: GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.BulkActionSideBar], payload)
    });
  };

  const RenderView = () => {
    switch (action) {
      case 'Status:Active':
        return (
          <Restore
            justify="left"
            affected={affected(action)}
            confirm={() => {
              reset();
              ctx.dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Active', false));
            }}
            cancel={reset}
          />
        );

      case 'Status:Deleted':
        return (
          <Delete
            justify="left"
            affected={affected(action)}
            confirm={() => {
              reset();
              ctx.dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Deleted', true));
            }}
            cancel={reset}
          />
        );

      case 'Status:Archived':
        return (
          <Archive
            justify="left"
            affected={affected(action)}
            confirm={() => {
              reset();
              ctx.dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Archived', true));
            }}
            cancel={reset}
          />
        );

      case 'Status:Inactive':
        return (
          <SaveForLater
            justify="left"
            affected={affected(action)}
            confirm={() => {
              reset();
              ctx.dispatch(new ApplyStatusToSelectedAction(props.filterKey, 'Inactive', true));
            }}
            cancel={reset}
          />
        );

      case 'Service:Add':
      case 'Service:Change':
        return (
          <ApplyService
            getServiceTags={() => getServiceTags().then((r) => (r.success ? r.result.Tags : []))}
            updateServiceTags={updateServiceTags}
            affected={affected(action)}
            selectedItems={() => GetSelectedItems(props.filterKey, ctx.state, props.smartSendItemsCache)}
            loggingSections={[EventSections.SmartSend, EventSections.SmartSendItem]}
            confirm={(service, serviceOption) => {
              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  [action, 'Service', 'ServiceOption'],
                  {
                    Service: service.Service,
                    ServiceOption: serviceOption
                  },
                  false
                )
              );
            }}
            quoteProviders={() =>
              getQuoteProviders().then((r) => {
                return r.success ? r.result.Providers : [];
              })
            }
            getQuotes={async (
              request,
              packageDimensions: Dimensions,
              quoteAmount: number,
              quoteSlug: string | null
            ) => {
              const r = await getQuoteOptions(
                request.provider,
                request.accountId,
                {
                  Recipient: request.recipient,
                  Sender: request.sender,
                  PackageDimensions: packageDimensions,
                  MarketplaceOrderLines: [{ Value: quoteAmount, Source: { Id: quoteSlug } }],
                  Value: quoteAmount
                } as SmartSendItemApiModel,
                null,
                request.includeTags,
                request.excludeTags
              );
              return r.success ? r.result.Quotes : [];
            }}
            cancel={close}
          />
        );

      case 'TariffCodes:Add':
        return (
          <ApplyTariffCodes
            affected={affected(action)}
            selectedItemCount={props.filter.selected.length}
            filterContext={props.filterKey}
            items={() =>
              GetSelectedItems(props.filterKey, ctx.state, props.smartSendItemsCache).filter((x) =>
                GenerateOptions(x, x.State.validation).includes(action)
              )
            }
            confirm={(codes) => {
              const descriptionCount = Object.values(codes).reduce(
                (total, value) => total + Object.keys(value).length,
                0
              );

              trackBulkActionApplied({
                surface: 'bulk-action-sidebar',
                action: 'apply-tariff-codes',
                affectedItemCount: affected(action),
                selectedItemCount: props.filter.selected.length,
                filterContext: props.filterKey,
                itemScope: 'bulk',
                descriptionCount: descriptionCount
              });

              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  [action, 'TariffCodes'],
                  {
                    TariffCodes: codes
                  },
                  false
                )
              );
            }}
          />
        );

      case 'Dimensions:Change':
      case 'Dimensions:Add':
        return (
          <ApplyDimensions
            showDefaultDimensions={SettingsCache.settings.Features.includes('DefaultDimensionsRule')}
            showDefaultWeight={SettingsCache.settings.Features.includes('DefaultWeightRule')}
            preSelect={action === 'Dimensions:Add'}
            affected={affected(action)}
            selectedItemCount={props.filter.selected.length}
            filterContext={props.filterKey}
            confirm={(dims) => {
              const changedFields = [
                dims.Weight > 0 && 'weight',
                dims.Height > 0 && 'height',
                dims.Width > 0 && 'width',
                dims.Length > 0 && 'length'
              ].filter((value): value is string => !!value);

              trackBulkActionApplied({
                surface: 'bulk-action-sidebar',
                action: 'apply-dimensions',
                affectedItemCount: affected(action),
                selectedItemCount: props.filter.selected.length,
                filterContext: props.filterKey,
                itemScope: 'bulk',
                changedFields,
                dimensions: {
                  weight: dims.Weight,
                  height: dims.Height,
                  width: dims.Width,
                  length: dims.Length
                }
              });

              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(props.filterKey, [action, 'Dimensions'], { Dimensions: dims }, false)
              );
            }}
            getPackages={() => {
              return getPackages().then((r) => {
                return r.success ? r.result.Packages : [];
              });
            }}
            getRules={(t) => {
              return getRules(t).then((r) => {
                return r.success ? r.result.Rules : [];
              });
            }}
            createRule={(r) => {
              return createRule(r).then((r) => r.success);
            }}
            updateRule={(r) => {
              return updateRule(r).then((r) => r.success);
            }}
            cancel={reset}
          />
        );

      case 'Download:Labels':
        return <DownloadLabels affected={affected(action)} cancel={reset} />;

      case 'PreferredCollectionDate':
        return (
          <ApplyPreferredCollectionDate
            affected={affected(action)}
            confirm={(force, date) => {
              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  ['PreferredCollectionDate'],
                  { PreferredCollectionDate: date },
                  force
                )
              );
            }}
            cancel={reset}
          />
        );

      case 'RecipientContactDetails:Add':
      case 'RecipientContactDetails:Change':
        return (
          <ApplyRecipientContactDetails
            affected={affected(action)}
            customer={ctx.state.customer || { Email: '', Phone: '' }}
            confirm={(customer) => {
              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  [action, 'RecipientContactDetails'],
                  { RecipientContactDetails: customer },
                  false
                )
              );
            }}
            cancel={reset}
          />
        );

      case 'Return':
        return (
          <ConfirmReturnCollection
            confirm={() => {
              const items = GetSelectedItems(props.filterKey, ctx.state, props.smartSendItemsCache);
              ctx.dispatch(new ArrangeReturnCollectionAction(items.map((x) => x.Id)));
              reset();
            }}
            cancel={reset}
            affected={affected(action)}
          />
        );

      case 'Tags':
        return (
          <Tags
            section={EventSections.BulkActionSideBar}
            confirm={(tags) => {
              reset();
              trackBulkActionApplied({
                surface: 'bulk-action-sidebar',
                action: 'apply-tags',
                affectedItemCount: affected(action),
                selectedItemCount: props.filter.selected.length,
                filterContext: props.filterKey,
                itemScope: 'bulk',
                tagCount: tags.length,
                tags: tags
              });
              ctx.dispatch(new BulkSaveSelectedAction(props.filterKey, ['Tags'], { Tags: tags }, false));
            }}
            getTags={() => {
              return getTags().then((r) => {
                return r.success ? r.result.Tags : [];
              });
            }}
            cancel={reset}
          />
        );

      case 'Download:CSV:Sold':
        return (
          <ExportToCsv
            mode="manual"
            fileName="SmartSend_SoldItems_Export"
            headers={SmartSendCsvExportSoldHeaders}
            data={() => {
              const data = GetSelectionBasedOnAction(props.filterKey, ctx.state, action, props.smartSendItemsCache).map(
                MapSmartSendSoldItemToCsv
              );
              return {
                fields: SmartSendCsvExportSoldHeaders(),
                data: data
              };
            }}
          />
        );

      case 'Download:CSV:Booked':
        return (
          <ExportToCsv
            mode="manual"
            fileName="SmartSend_BookedItems_Export"
            headers={SmartSendCsvExportBookedHeaders}
            data={() => {
              const data = GetSelectionBasedOnAction(props.filterKey, ctx.state, action, props.smartSendItemsCache).map(
                MapSmartSendBookedItemToCsv
              );
              return {
                fields: SmartSendCsvExportBookedHeaders(),
                data: data
              };
            }}
          />
        );

      case 'Download:CSV:Orders':
        return (
          <ExportToCsv
            mode="manual"
            fileName="SmartSend_Orders_Export"
            headers={SmartSendCsvExportOrderHeaders}
            data={() => {
              const data = GetSelectionBasedOnAction(props.filterKey, ctx.state, action, props.smartSendItemsCache).map(
                MapOrderToCsv
              );
              return {
                fields: SmartSendCsvExportOrderHeaders(),
                data: data
              };
            }}
          />
        );

      case 'Dispatch:Confirm':
        return (
          <ConfirmDispatch
            filterKey={props.filterKey}
            cancel={reset}
            affected={affected('MarkAsDispatched')}
            confirm={() => onActionClick('Dispatch')}
          />
        );

      case 'Extras':
        return (
          <Extras
            include={[]}
            exclude={ProtectionExtraIds}
            showDefaultRule={SettingsCache.settings.Features.includes('DefaultExtrasRule')}
            affected={affected(action)}
            selectedItemCount={props.filter.selected.length}
            filterContext={props.filterKey}
            getSelectedItems={() => GetSelectedItems(props.filterKey, ctx.state, props.smartSendItemsCache)}
            confirm={(extras) => {
              trackBulkActionApplied({
                surface: 'bulk-action-sidebar',
                action: 'apply-extras',
                affectedItemCount: affected(action),
                selectedItemCount: props.filter.selected.length,
                filterContext: props.filterKey,
                itemScope: 'bulk',
                extraIds: extras.map((extra) => extra.Id),
                extras: extras.map((extra) => ({
                  id: extra.Id,
                  name: extra.Name,
                  provider: extra.Provider
                }))
              });

              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  ['Extras'],
                  {
                    Extras: extras.map((x) => {
                      return { ...x, Total: 0, Price: 0, Vat: 0 };
                    })
                  },
                  false
                )
              );
            }}
            cancel={reset}
            getRules={(t) => {
              return getRules(t).then((r) => {
                return r.success ? r.result.Rules : [];
              });
            }}
            createRule={(r) => {
              return createRule(r).then((r) => r.success);
            }}
            updateRule={(r) => {
              return updateRule(r).then((r) => r.success);
            }}
          />
        );

      case 'Protection':
        return (
          <Protection
            affected={affected(action)}
            selectedItemCount={props.filter.selected.length}
            filterContext={props.filterKey}
            protectionIds={ProtectionExtraIds}
            showDefaultRule={SettingsCache.settings.Features.includes('DefaultExtrasRule')}
            confirm={(extras) => {
              trackBulkActionApplied({
                surface: 'bulk-action-sidebar',
                action: 'apply-protection',
                affectedItemCount: affected(action),
                selectedItemCount: props.filter.selected.length,
                filterContext: props.filterKey,
                itemScope: 'bulk',
                protectionKey: generateProtectionKey(extras),
                extraIds: extras.map((extra) => extra.Id)
              });

              reset();
              ctx.dispatch(
                new BulkSaveSelectedAction(
                  props.filterKey,
                  ['Extras'],
                  {
                    Extras: extras.map((x) => {
                      return { ...x, Total: 0, Price: 0, Vat: 0 };
                    })
                  },
                  false
                )
              );
            }}
            cancel={reset}
            getRules={(t) => {
              return getRules(t).then((r) => {
                return r.success ? r.result.Rules : [];
              });
            }}
            createRule={(r) => {
              return createRule(r).then((r) => r.success);
            }}
            updateRule={(r) => {
              return updateRule(r).then((r) => r.success);
            }}
          />
        );

      default:
        return (
          <P2GBox mb={2}>
            <SmartSendActionList
              affected={affected}
              exclude={props.exclude}
              onActionClick={onActionClick}
              bulkOptions={props.filter.bulkOptions}
              onClose={close}
            />
          </P2GBox>
        );
    }
  };

  if (['Service:Add', 'Service:Change'].includes(action)) {
    return (
      <Suspense>
        <RenderView />
      </Suspense>
    );
  }

  return (
    <P2GSidePanel
      open={props.open}
      onClose={close}
      anchor={'right'}
      width="600px"
      title={GetBulkActionTitle(t, action)}
      actions={
        action === 'LIST' ? (
          <PanelButton
            text={t('CLOSE_ACTIONS_BUTTON', { defaultValue: 'Close Actions' })}
            colour="tertiary"
            onClick={close}
          />
        ) : null
      }
    >
      {props.open && (
        <Tagged tag="panel-actions">
          <Suspense>
            <RenderView />
          </Suspense>
        </Tagged>
      )}
    </P2GSidePanel>
  );
}
